import { cn } from '@/lib/utils'
import { Event } from '@nostr/tools/wasm'
import { useMemo } from 'react'
import ExternalLink from '../ExternalLink'

type Detail = {
  label: string
  values: string[]
  link?: boolean
}

export default function CodeSnippet({ event, className }: { event: Event; className?: string }) {
  const details = useMemo<Detail[]>(() => {
    const details: Detail[] = []
    const first = (name: string) => event.tags.find((t) => t[0] === name)?.[1]
    const all = (name: string) =>
      event.tags
        .filter((t) => t[0] === name)
        .map((t) => t[1])
        .filter(Boolean)

    const name = first('name')
    const language = first('l')
    const extension = first('extension')
    const description = first('description')
    const runtime = first('runtime')
    const licenses = all('license')
    const deps = all('dep')
    const repos = all('repo')

    if (name) details.push({ label: 'Name', values: [name] })
    if (language) details.push({ label: 'Language', values: [language] })
    if (extension) details.push({ label: 'Extension', values: [extension] })
    if (description) details.push({ label: 'Description', values: [description] })
    if (runtime) details.push({ label: 'Runtime', values: [runtime] })
    if (licenses.length) details.push({ label: 'License', values: licenses })
    if (deps.length) details.push({ label: 'Dependencies', values: deps })
    if (repos.length) details.push({ label: 'Repository', values: repos, link: true })
    return details
  }, [event])

  return (
    <div className={cn('space-y-2', className)}>
      <div className="font-medium">Code Snippet</div>
      {details.length > 0 && (
        <div className="space-y-1 text-sm text-muted-foreground">
          {details.map((detail) => (
            <div key={detail.label}>
              <span className="font-medium text-foreground">{detail.label}: </span>
              {detail.values.map((value, index) => (
                <span key={`${detail.label}-${value}-${index}`}>
                  {index > 0 && <span>, </span>}
                  {detail.link ? <ExternalLink url={value} /> : value}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}
      {event.content && (
        <pre className="mt-2 rounded-lg bg-muted p-3 overflow-x-auto text-sm font-mono whitespace-pre">
          {event.content}
        </pre>
      )}
    </div>
  )
}
